// src/pages/PwpPaywall.jsx
// Piano With Pan Paywall V9
// ✅ META PIXEL ADDED: ViewContent (paywall view) + InitiateCheckout (plan CTA click)
// ✅ "Free Plan" (was "Free Forever") — no implied permanence
// ✅ "forever" removed from all copy
// ✅ Founder video thumbnail card (seek-to-2s via onLoadedMetadata)
// ✅ Comprehensive GA tracking — page view, scroll depth, plan impressions,
//    song clicks, FAQ toggles, video open/close/play/pause/complete,
//    checkout begin/complete/error, back/home nav

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { hasActivePass, setMockPass } from "../services/passStorage";
import { supabase } from "../supabaseClient";
import PianoLoadingScreen from "../components/PianoLoadingScreen";

// ✅ META PIXEL ADDED
import { pixelViewPaywall, pixelBeginCheckout } from "../analytics/metaPixel";

/* ═══════════════════════════════════════════════════════════
   GOOGLE ANALYTICS TRACKING
═══════════════════════════════════════════════════════════ */
const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

const trackPageView = (page) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-TZC5XT1878', { page_path: page });
  }
};

function useQuery(search) {
  return useMemo(() => new URLSearchParams(search), [search]);
}

function sanitizeBackPath(back) {
  if (typeof back !== "string") return "/level-2";
  if (!back.startsWith("/")) return "/level-2";
  if (back.startsWith("//")) return "/level-2";
  return back;
}

async function postEntitlement(accessToken) {
  const res = await fetch("/api/pwp-entitlement", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || `Entitlement error (${res.status})`);
  }
  return json;
}

/* ── What unlocks ── */
const UNLOCKS = [
  { emoji: "🎵", title: "All 10 guided piano songs",        sub: "Twinkle Twinkle, Happy Birthday, Jingle Bells + 7 more" },
  { emoji: "🔓", title: "Levels 4–10 unlocked — full game", sub: "Free tier gives Levels 1–3 unlimited. Premium unlocks everything." },
  { emoji: "🐉", title: "Full Treble Town story",            sub: "8 stars, boss battles, and the final showdown with Doron" },
  { emoji: "🏆", title: "Trophies & star rewards",           sub: "Every level completed earns stars and trophy milestones" },
  { emoji: "🎹", title: "Virtual piano + real piano mode",   sub: "Works with microphone for real piano practice too" },
  { emoji: "📱", title: "Works on any device",               sub: "Mobile, tablet, or laptop — no app download needed" },
];

/* ── Song list ── */
const ALL_SONGS = [
  "Twinkle Twinkle Little Star",
  "Happy Birthday",
  "Ode to Joy",
  "Machli Jal Ki Rani",
  "Jingle Bells",
  "Incy Wincy Spider",
  "London Bridge",
  "National Anthem of India",
  "Row Row Row Your Boat",
  "Ancient Melody",
];
const SONG_EMOJIS = ["🌟","🎂","🎼","🐠","🔔","🕷️","🌉","🇮🇳","🚣","🏛️"];

/* ── Plan definitions ── */
const coreBenefits = [
  "All 10 levels unlocked — Levels 4–10 exclusive to Premium",
  "Levels 4–10: Machli Jal Ki Rani, Jingle Bells, Incy Wincy Spider + 4 more",
  "Complete the Treble Town quest — 8 stars + final battle",
  "10 piano songs with guided note-by-note play",
  "Stars, trophies and completion rewards",
  "Virtual piano + real piano microphone mode",
];

const plans = [
  {
    planId: "free",
    title: "Free Plan",
    priceLabel: "₹0",
    originalPrice: null,
    isLaunchOffer: false,
    isFree: true,
    durationDays: null,
    highlight: false,
    badge: null,
    badgeStyle: null,
    cta: "Start Free Now",
    muted: false,
    priceNumeric: 0,
    freeBenefits: [
      { icon: "✓", text: "Levels 1–3: 3 songs — Twinkle Twinkle, Happy Birthday, Ode to Joy" },
      { icon: "✓", text: "Unlimited play — no daily time limit on free levels" },
      { icon: "✓", text: "Learn Your Notes tutorial (unlimited)" },
      { icon: "✓", text: "Practice Mode (unlimited)" },
    ],
    hasLevel2Trial: false,
  },
  {
    planId: "monthly",
    title: "Full Access Monthly",
    priceLabel: "₹299",
    originalPrice: "₹499",
    discountPercent: 40,
    isLaunchOffer: true,
    isFree: false,
    durationDays: 30,
    highlight: true,
    badge: "MOST POPULAR",
    badgeStyle: "popular",
    cta: "Get Premium →",
    muted: false,
    priceNumeric: 299,
    freeBenefits: null,
    hasLevel2Trial: false,
  },
];

const faqs = [
  {
    q: "Do I need a real piano or keyboard?",
    a: "No! Our virtual piano works on any device. A real keyboard makes it better — turn on your microphone for real-time feedback.",
  },
  {
    q: "How much time per day?",
    a: "Levels 1–3 are completely free with no time limits — your child can play as much as they like. Levels 4–10 require Premium (₹299), which gives full unlimited access.",
  },
  {
    q: "What does my child learn?",
    a: "Piano notes (C-B, sharps), rhythm, 10 songs starting with Twinkle Twinkle. They'll learn by playing and having fun.",
  },
  {
    q: "What happens after 30 days?",
    a: "Your pass expires. Progress is saved. You keep free access to Levels 1–3 with no time limits. Renew anytime to continue Premium.",
  },
];

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export default function PwpPaywall() {
  const loc      = useLocation();
  const navigate = useNavigate();
  const query    = useQuery(loc.search);

  const back          = sanitizeBackPath(query.get("next") || "/game-menu");
  const reason        = query.get("reason") || "";
  const paymentLinkId = query.get("paymentLinkId") || null;

  const [serverActive,   setServerActive]   = useState(null);
  const [busyPlanId,     setBusyPlanId]     = useState(null);
  const [errMsg,         setErrMsg]         = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [faqOpen,        setFaqOpen]        = useState(null);

  const PAYWALL_MODE = (process.env.REACT_APP_PWP_PAYWALL_MODE || "mock").toLowerCase();
  const isMock      = !(PAYWALL_MODE === "razorpay" || PAYWALL_MODE === "live");
  const isExpired   = hasActivePass() === false && reason === "";

  /* ── GA + META PIXEL: page view on mount ── */
  useEffect(() => {
    trackPageView('/paywall');
    trackEvent('paywall_view', {
      reason:          reason || 'direct',
      is_expired:      isExpired,
      back_path:       back,
      payment_link_id: paymentLinkId || 'none',
    });
    
    // ✅ META PIXEL: ViewContent event when paywall loads
    pixelViewPaywall();
  }, [reason, isExpired, back, paymentLinkId]);

  /* ── GA: plan impressions ── */
  useEffect(() => {
    plans.forEach(plan => {
      trackEvent('plan_impression', {
        plan_id:      plan.planId,
        plan_title:   plan.title,
        price:        plan.priceNumeric,
        is_highlighted: plan.highlight,
      });
    });
  }, []);

  /* ── GA: scroll depth milestones (25 / 50 / 75 / 100%) ── */
  useEffect(() => {
    const thresholds = [25, 50, 75, 100];
    const fired = new Set();
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const pct   = total > 0 ? Math.round((window.scrollY / total) * 100) : 0;
      thresholds.forEach(t => {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          trackEvent('paywall_scroll_depth', { depth_percent: t });
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── GA: song list view ── */
  useEffect(() => {
    trackEvent('song_list_shown', { song_count: ALL_SONGS.length });
  }, []);

  /* ── Payment link check (return from Razorpay) ── */
  useEffect(() => {
    if (!paymentLinkId) { setServerActive(false); return; }
    trackEvent('payment_link_check', { payment_link_id: paymentLinkId });

    let alive = true;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const session  = data?.session;
        if (!session?.access_token) { if (alive) setServerActive(false); return; }
        const ent = await postEntitlement(session.access_token);
        if (!alive) return;
        if (ent?.active) {
          trackEvent('payment_success', { payment_link_id: paymentLinkId });
          navigate(`/pwp-payment-success?next=${encodeURIComponent(back)}`, { replace: true });
          return;
        }
        setServerActive(false);
      } catch { if (alive) setServerActive(false); }
    })();
    return () => { alive = false; };
  }, [navigate, back, paymentLinkId]);

  /* ── Buy: mock ── */
  const buyMock = async (plan) => {
    setErrMsg(""); setBusyPlanId(plan.planId);
    trackEvent('checkout_begin', { plan_id: plan.planId, price: plan.priceNumeric, plan_title: plan.title, payment_mode: 'mock' });
    
    // ✅ META PIXEL: InitiateCheckout when user clicks pay
    pixelBeginCheckout(plan.planId, plan.priceNumeric);
    
    try {
      setMockPass({ planId: plan.planId, durationDays: plan.durationDays });
      trackEvent('checkout_complete_mock', { plan_id: plan.planId, price: plan.priceNumeric });
      navigate(back, { replace: true });
    } finally { setBusyPlanId(null); }
  };

  /* ── Buy: live ── */
  const buyLive = async (plan) => {
    setErrMsg(""); setBusyPlanId(plan.planId);
    trackEvent('checkout_begin', { plan_id: plan.planId, price: plan.priceNumeric, plan_title: plan.title, payment_mode: 'live' });
    
    // ✅ META PIXEL: InitiateCheckout when user clicks pay
    pixelBeginCheckout(plan.planId, plan.priceNumeric);
    
    try {
      const { data } = await supabase.auth.getSession();
      const session  = data?.session;
      if (!session?.access_token) {
        trackEvent('checkout_login_required', { plan_id: plan.planId });
        setBusyPlanId(null);
        navigate(`/login?back=${encodeURIComponent(`/pwp-paywall?next=${encodeURIComponent(back)}`)}`);
        return;
      }
      const r = await fetch("/api/pwp-create-payment-link", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ planId: plan.planId, back }),
      });
      const text = await r.text();
      let data2 = null;
      try { data2 = JSON.parse(text); }
      catch { data2 = { ok: false, error: "Invalid JSON from server", raw: text }; }
      if (!r.ok || !data2?.ok || !data2?.shortUrl) {
        trackEvent('checkout_error', { plan_id: plan.planId, error: data2?.error || 'payment_link_failed' });
        setErrMsg(data2?.error || "Could not create payment link.");
        setBusyPlanId(null); return;
      }
      trackEvent('checkout_redirect_to_payment', { plan_id: plan.planId, payment_url: data2.shortUrl });
      window.location.href = data2.shortUrl;
    } catch (e) {
      trackEvent('checkout_error', { plan_id: plan.planId, error: e?.message || 'network_error' });
      setErrMsg(e?.message || "Network error while creating payment link.");
      setBusyPlanId(null);
    }
  };

  const onBuy = (plan) => (isMock ? buyMock(plan) : buyLive(plan));

  const onFreePlan = () => {
    trackEvent('free_plan_click', { source: 'paywall_free_plan_cta' });
    navigate("/game-menu");
  };

  const onLevel2Trial = async () => {
    trackEvent('level_2_trial_click');
    try {
      const { data } = await supabase.auth.getSession();
      const session  = data?.session;
      if (!session?.access_token) {
        trackEvent('level_2_trial_login_required');
        navigate(`/login?back=${encodeURIComponent("/level-2-trial")}`);
        return;
      }
      navigate("/level-2-trial");
    } catch (e) {
      console.error("Level 2 trial error:", e);
      navigate("/login?back=" + encodeURIComponent("/level-2-trial"));
    }
  };

  const handleVideoModalOpen = () => {
    trackEvent('founder_video_open', { source: 'paywall_thumbnail' });
    setShowVideoModal(true);
  };

  const handleFaqToggle = (idx, question) => {
    const isOpening = faqOpen !== idx;
    setFaqOpen(isOpening ? idx : null);
    trackEvent('faq_toggle', { question, action: isOpening ? 'open' : 'close', index: idx });
  };

  if (serverActive === null && !paymentLinkId) {
    return <PianoLoadingScreen message="Checking your pass" />;
  }

  /* ══════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F4FF] via-white to-[#FFF7ED] font-sans">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@700;800;900&display=swap');
        .pw-card-popular { box-shadow: 0 8px 40px rgba(124,58,237,0.18), 0 2px 8px rgba(124,58,237,0.1); }
        @keyframes pw-pulse {
          0%,100% { box-shadow: 0 8px 40px rgba(124,58,237,0.18); }
          50%      { box-shadow: 0 12px 48px rgba(124,58,237,0.32); }
        }
        .pw-card-popular { animation: pw-pulse 2.5s ease-in-out infinite; }
        @keyframes pw-pop {
          from { transform: scale(0.94) translateY(16px); opacity: 0; }
          to   { transform: scale(1) translateY(0);       opacity: 1; }
        }
        .pw-modal { animation: pw-pop 0.35s cubic-bezier(.34,1.56,.64,1) both; }
      `}</style>

      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 py-8 sm:py-12">

        {/* ── Back + Home ── */}
        <div className="mb-6 flex items-center justify-between">
          <Link
            to={back}
            onClick={() => trackEvent('back_button_click', { from: 'paywall', to: back })}
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 transition"
          >
            ← Back
          </Link>
          <Link
            to="/home"
            onClick={() => trackEvent('home_button_click', { from: 'paywall' })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border-2 border-slate-200 text-sm font-bold text-slate-700 hover:border-slate-300 hover:text-slate-900 transition"
          >
            🏠 Home
          </Link>
        </div>

        {/* ── Expired banner ── */}
        {isExpired && (
          <div className="mb-6 rounded-2xl border-2 border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-3">
            <span className="text-2xl flex-shrink-0">👋</span>
            <div>
              <div className="font-extrabold text-amber-900 text-sm" style={{fontFamily:"'Fredoka One',cursive"}}>
                Welcome back! Your pass has expired.
              </div>
              <div className="text-amber-700 text-sm font-semibold mt-0.5">
                Your progress is saved — pick up exactly where you left off once you renew.
              </div>
            </div>
          </div>
        )}

        {/* ── Hero ── */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 border border-purple-100 px-4 py-1.5 text-xs font-extrabold text-purple-700 mb-4">
            🐉 Treble Town Full Quest
          </div>
          <h1
            style={{fontFamily:"'Fredoka One',cursive"}}
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight"
          >
            {isExpired ? "Renew to keep playing 🎹" : "Unlock the full adventure"}
          </h1>
          <p className="mt-3 text-base sm:text-lg font-bold text-slate-600 max-w-xl mx-auto">
            {reason === "premium_required" || reason === "free_trial_complete"
              ? "Levels 4–10 are exclusive to Premium. Unlock the full quest and defeat all the bosses!"
              : "Levels 1–3 are free. Get Premium to unlock Levels 4–10 and complete the full adventure."}
          </p>

          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-2 text-sm font-extrabold text-amber-800">
            🎉 Launch offer — Monthly pass just ₹299 (was ₹499) · 40% OFF
          </div>
        </div>

        {/* ── What you unlock ── */}
        <div className="mb-10 rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-purple-50 to-indigo-50">
            <div className="text-sm font-extrabold text-slate-800" style={{fontFamily:"'Fredoka One',cursive"}}>
              What you get with Premium
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            {UNLOCKS.map((item) => (
              <div key={item.title} className="flex items-start gap-3 px-5 py-4">
                <span className="text-2xl flex-shrink-0 mt-0.5">{item.emoji}</span>
                <div>
                  <div className="text-sm font-extrabold text-slate-900">{item.title}</div>
                  <div className="text-xs text-slate-500 font-semibold mt-0.5">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── 10 Songs ── */}
        <div className="mb-10 rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 overflow-hidden">
          <div className="px-5 py-4 border-b-2 border-purple-200 bg-white/50">
            <div className="text-base sm:text-lg font-extrabold text-slate-900 text-center" style={{fontFamily:"'Fredoka One',cursive"}}>
              🎵 10 Piano Songs Your Child Will Learn
            </div>
            <div className="text-xs sm:text-sm font-semibold text-slate-600 text-center mt-1">
              From Twinkle Twinkle to the National Anthem — complete guided lessons
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-5 py-5">
            {ALL_SONGS.map((song, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-purple-100 shadow-sm hover:shadow-md transition cursor-default"
                onClick={() => trackEvent('song_item_click', { song, level: idx + 1 })}
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-extrabold text-sm">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-extrabold text-slate-900 leading-tight">{song}</div>
                </div>
                <div className="flex-shrink-0 text-lg">{SONG_EMOJIS[idx]}</div>
              </div>
            ))}
          </div>
          <div className="px-5 pb-4 text-center">
            <div className="text-xs font-bold text-purple-700">
              ✨ All songs include note-by-note guidance, rhythm training, and progress tracking
            </div>
          </div>
        </div>

        {/* ── Pricing ── */}
        <div className="mb-10">
          <div className="text-center mb-6">
            <div style={{fontFamily:"'Fredoka One',cursive"}} className="text-xl sm:text-2xl font-extrabold text-slate-900">
              Choose your pass
            </div>
            <div className="text-sm font-bold text-slate-500 mt-1">
              Levels 1–3 are free · Unlock the full game with Premium
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start max-w-3xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.planId}
                className={[
                  "rounded-2xl border overflow-hidden transition-all",
                  plan.highlight
                    ? "border-purple-300 ring-2 ring-purple-200 pw-card-popular"
                    : plan.isFree
                    ? "border-emerald-200 bg-gradient-to-b from-emerald-50 to-white"
                    : "border-slate-200",
                ].join(" ")}
                style={{ background: plan.isFree ? undefined : "#fff" }}
              >
                <div className={["px-5 pt-5 pb-4", plan.highlight ? "bg-gradient-to-b from-purple-50 to-white" : ""].join(" ")}>
                  <div className="flex items-start justify-between gap-2">
                    <div style={{fontFamily:"'Fredoka One',cursive"}} className="text-lg font-extrabold text-slate-900">
                      {plan.title}
                    </div>
                    {plan.badge && (
                      <span className={[
                        "shrink-0 rounded-full text-[11px] font-extrabold px-2.5 py-0.5",
                        plan.badgeStyle === "popular" ? "bg-purple-600 text-white" : "bg-emerald-600 text-white",
                      ].join(" ")}>
                        {plan.badge}
                      </span>
                    )}
                  </div>

                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-3xl font-black text-slate-900">{plan.priceLabel}</span>
                    {plan.originalPrice && (
                      <>
                        <span className="text-base font-semibold text-slate-400 line-through">{plan.originalPrice}</span>
                        {plan.discountPercent && (
                          <span className="text-xs font-extrabold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                            {plan.discountPercent}% OFF
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {plan.isLaunchOffer && (
                    <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                      🎉 Launch price
                    </div>
                  )}

                  <button
                    type="button"
                    disabled={busyPlanId === plan.planId}
                    onClick={() => {
                      trackEvent('plan_cta_click', {
                        plan_id:    plan.planId,
                        plan_title: plan.title,
                        price:      plan.priceNumeric,
                        is_free:    plan.isFree,
                      });
                      plan.isFree ? onFreePlan() : onBuy(plan);
                    }}
                    className={[
                      "mt-4 w-full rounded-xl py-2.5 text-sm font-extrabold border transition",
                      plan.isFree
                        ? "bg-gradient-to-b from-[#34D399] to-[#10B981] text-white border-emerald-700 shadow-[0_4px_0_rgba(5,150,105,0.35)] hover:brightness-105 active:translate-y-[1px]"
                        : plan.highlight
                        ? "bg-gradient-to-b from-[#8B5CF6] to-[#7C3AED] text-white border-purple-700 shadow-[0_6px_0_rgba(88,28,135,0.35)] hover:brightness-105 active:translate-y-[2px]"
                        : "bg-gradient-to-b from-[#34D399] to-[#10B981] text-white border-emerald-700 shadow-[0_4px_0_rgba(5,150,105,0.35)] hover:brightness-105 active:translate-y-[1px]",
                    ].join(" ")}
                  >
                    {busyPlanId === plan.planId ? "Processing…" : plan.cta}
                  </button>
                </div>

                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  {plan.isFree ? (
                    <>
                      <div className="text-[11px] font-extrabold text-slate-600 uppercase tracking-wide mb-2">What's Free</div>
                      <ul className="space-y-1.5">
                        {plan.freeBenefits.map((b) => (
                          <li key={b.text} className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-0.5 flex-shrink-0">{b.icon}</span>
                            <span className="text-xs text-slate-700 font-semibold">{b.text}</span>
                          </li>
                        ))}
                      </ul>
                      {plan.hasLevel2Trial && (
                        <div className="mt-4 pt-4 border-t border-emerald-100">
                          <div className="text-[11px] font-extrabold text-emerald-700 uppercase tracking-wide mb-2">🎁 One-Time Bonus</div>
                          <button
                            type="button"
                            onClick={onLevel2Trial}
                            className="w-full rounded-xl py-2.5 text-sm font-extrabold border-2 border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 transition"
                          >
                            Try Level 2 Free
                          </button>
                          <div className="text-[10px] text-emerald-700 font-bold mt-1.5 text-center">One time only · Requires login</div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-[11px] font-extrabold text-slate-600 uppercase tracking-wide mb-2">Everything Included</div>
                      <ul className="space-y-1.5">
                        {coreBenefits.map((txt) => (
                          <li key={txt} className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5 flex-shrink-0">✓</span>
                            <span className="text-xs text-slate-700 font-semibold">{txt}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {errMsg && (
            <div className="mt-4 rounded-xl bg-red-50 border-2 border-red-200 px-4 py-3 text-center text-sm font-bold text-red-700">
              {errMsg}
            </div>
          )}
        </div>

        {/* ── Founder Video — thumbnail card ── */}
        <div className="mb-10 rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 overflow-hidden">
          <div className="px-5 py-4 border-b-2 border-purple-200">
            <div className="text-sm font-extrabold text-slate-800" style={{fontFamily:"'Fredoka One',cursive"}}>
              🎬 Hear from the creator
            </div>
          </div>
          <div className="px-5 py-5">
            <div className="text-sm font-semibold text-slate-700 mb-4 text-center">
              Meet Nishat Chadha, music educator and creator of Piano With Pan
            </div>

            <button
              type="button"
              onClick={handleVideoModalOpen}
              className="group relative w-full max-w-lg mx-auto block rounded-2xl overflow-hidden shadow-lg border-2 border-purple-200 hover:border-purple-400 transition-all hover:shadow-xl"
              aria-label="Watch founder intro video"
            >
              {/* Thumbnail frame */}
              <video
                src="/videos/founder-intro.mp4"
                className="w-full aspect-video object-cover block pointer-events-none"
                preload="metadata"
                muted
                playsInline
                tabIndex={-1}
                aria-hidden="true"
                onLoadedMetadata={(e) => {
                  try { e.target.currentTime = 2; } catch {}
                }}
              />

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

              {/* Play button + label */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                  <div style={{
                    width: 0, height: 0,
                    borderTop: "10px solid transparent",
                    borderBottom: "10px solid transparent",
                    borderLeft: "18px solid #7C3AED",
                    marginLeft: "4px",
                  }} />
                </div>
                <span className="text-white font-extrabold text-sm sm:text-base drop-shadow-lg" style={{fontFamily:"'Fredoka One',cursive"}}>
                  Watch Intro Video
                </span>
              </div>

              {/* Duration badge */}
              <div className="absolute bottom-3 right-3 bg-black/70 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">
                ~2 min
              </div>
            </button>
          </div>
        </div>

        {/* ── FAQ ── */}
        <div className="mb-10">
          <div style={{fontFamily:"'Fredoka One',cursive"}} className="text-xl sm:text-2xl font-extrabold text-slate-900 text-center mb-6">
            Questions parents ask
          </div>
          <div className="space-y-3 max-w-2xl mx-auto">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={[
                  "rounded-xl border-2 overflow-hidden transition-all",
                  faqOpen === idx ? "border-purple-300 bg-purple-50" : "border-slate-200 bg-white",
                ].join(" ")}
              >
                <button
                  type="button"
                  onClick={() => handleFaqToggle(idx, faq.q)}
                  className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-purple-50/50 transition"
                >
                  <span className="text-sm font-extrabold text-slate-900">{faq.q}</span>
                  <span className={["text-lg font-black transition-transform", faqOpen === idx ? "rotate-45" : ""].join(" ")}>
                    +
                  </span>
                </button>
                {faqOpen === idx && (
                  <div className="px-5 pb-4 text-sm font-semibold text-slate-700 leading-relaxed border-t-2 border-purple-200 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Final CTA ── */}
        <div className="text-center">
          <button
            onClick={() => {
              trackEvent('final_cta_click', { plan_id: 'monthly', location: 'bottom' });
              onBuy(plans.find(p => p.planId === "monthly"));
            }}
            disabled={busyPlanId === "monthly"}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-b from-[#8B5CF6] to-[#7C3AED] text-white font-extrabold text-lg border-2 border-purple-700 shadow-[0_6px_0_rgba(88,28,135,0.35)] hover:brightness-105 active:translate-y-[2px] transition"
          >
            {busyPlanId === "monthly" ? "Processing…" : "Get Premium Access →"}
          </button>
        </div>

      </div>

      {/* ── Video Modal ── */}
      {showVideoModal && (
        <div
          onClick={() => { trackEvent('founder_video_close', { source: 'backdrop' }); setShowVideoModal(false); }}
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl border-2 border-slate-900 max-w-2xl w-full overflow-hidden pw-modal"
          >
            <div className="relative aspect-video bg-black">
              <video
                src="/videos/founder-intro.mp4"
                controls autoPlay playsInline controlsList="nodownload"
                className="w-full h-full object-cover"
                onPlay={() => trackEvent('founder_video_play')}
                onPause={() => trackEvent('founder_video_pause')}
                onEnded={() => trackEvent('founder_video_complete')}
              />
            </div>
            <div className="p-4 text-center">
              <button
                onClick={() => { trackEvent('founder_video_close', { source: 'close_button' }); setShowVideoModal(false); }}
                className="text-sm font-bold text-slate-600 hover:text-slate-900 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}